import * as colors from "@ant-design/colors";
import Big from "big.js";
import Color from "color";
import _ from "es-toolkit/compat";

import {
  PipelineTaskEnvironment,
  PipelineYAML,
} from "../components/PipelineYamlEditor";
import type { ImageArchitecture } from "../hooks/atoms";
import type {
  PipelineJobResultChoices,
  PipelineJobStatusChoices,
  TaskInstanceResultChoices,
  TaskInstanceStatusChoices,
} from "../screens/__generated__/PipelineJobDetailQuery.graphql";
import type { ByteUnit } from "./types";

export const cursor_to_offset = (cursor: string): number | undefined => {
  const x = _.nth(window.atob(cursor).split(":"), 1);
  return x ? parseInt(x, 10) : undefined;
};

export const offset_to_cursor = (offset: number): string => {
  return window.btoa(`arrayconnection:${offset}`);
};

type ResolvedGlobalId = readonly [string, string];

export const from_global_id = (global_id: string): ResolvedGlobalId => {
  let globalId: string;

  try {
    globalId = decodeURIComponent(window.atob(global_id));
  } catch (error) {
    console.error("Invalid base64 string:", global_id);
    return ["", ""];
  }

  if (!globalId.includes(":")) {
    return ["", globalId];
  }

  const resolvedGlobalId = globalId.split(":", 2);
  if (resolvedGlobalId.length < 2) {
    return ["", resolvedGlobalId[0]];
  }

  return [resolvedGlobalId[0], resolvedGlobalId[1]] as const;
};

export const to_global_id = (type: string, id: string): string => {
  const globalId = `${type}:${id}`;
  return window.btoa(globalId);
};

// from antd/lib/_util/colors.d.ts

export const statusColors = [
  "pink",
  "red",
  "yellow",
  "orange",
  "cyan",
  "green",
  "blue",
  "purple",
  "geekblue",
  "magenta",
  "volcano",
  "gold",
  "lime",
] as const;

export type PresetColorType = (typeof statusColors)[number];

type Status = TaskInstanceStatusChoices | PipelineJobStatusChoices;

export const getStatusColor = (status: Status) => {
  switch (status) {
    case "PENDING":
    case "WAITING":
    case "PAUSED":
    case "PREPARING":
    case "PREPARED":
    case "CREATING":
    case "PULLING":
    case "SCHEDULED":
    case "TERMINATING":
      return "blue";
    case "RUNNING":
      return "green";
    case "ERROR":
      return "red";
    case "CANCELLING":
    case "CANCELLED":
    case "SKIPPED":
      return "#999";
    case "TERMINATED":
      return "#666";
    case "PENDING_APPROVAL":
      return colors.geekblue[6];
    case "RETRYING":
      return "orange";
    default:
      return "white";
  }
};

export const getStatusTextColor = (status: Status = "PENDING") => {
  const colorName = getStatusColor(status);
  const color = Color(colorName);

  if (_.keys(colors).includes(colorName)) {
    // eslint-disable-next-line
    //@ts-ignore
    return colors[colorName][7];
  } else {
    const bgColor = Color(getStatusBgColor(status));
    return bgColor.luminosity() < 0.6 ? "#fff" : color.darken(0.8).hex();
  }
};

export const getStatusBgColor = (status: Status) => {
  const colorName = getStatusColor(status);
  const color = Color(colorName);

  if (_.keys(colors).includes(colorName)) {
    // eslint-disable-next-line
    //@ts-ignore
    return colors[colorName][0];
  } else {
    const bgColor = color.isDark() ? color.lighten(0.9) : color.lighten(0.5);
    return bgColor.hex();
  }
};

export const getResultColor = (
  result: TaskInstanceResultChoices | PipelineJobResultChoices
) => {
  let color = "lightgrey";
  switch (result) {
    case "SUCCESS":
      color = "green";
      break;
    case "FAILURE":
      color = "red";
      break;
    case "UNDEFINED":
      color = "lightgrey";
      break;
    default:
      color = "white";
      break;
  }
  return color;
};

export const getStatusInfoColor = (statusInfo: string) => {
  switch (statusInfo) {
    case "idle-timeout":
    case "user-requested":
    case "scheduled":
    case "self-terminated":
      return "green";
    case "failed-to-start":
    case "creation-failed":
    case "no-available-instances":
      return "red";
    default:
      return undefined;
  }
};

export const isTransitionStatus = (status: Status) => {
  return [
    "PENDING",
    "PREPARING",
    "CREATING",
    "PULLING",
    "TERMINATING",
    "CANCELLING",
    "RETRYING",
  ].includes(status);
};

export const getWebServerEndpoint = () => {
  return (
    window.__RUNTIME_CONFIG__.FRONTEND_RUNTIME_WEBSERVER_ENDPOINT ||
    localStorage.getItem("Backend_AI_FastTrack.endpoint")
  );
};

export const isFinalStatus = (status?: string) => {
  return _.includes(["TERMINATED", "CANCELLED", "ERROR"], status);
};

export const numberMinRule = (min: number, message: string) => () => ({
  validator(form: any, value: string) {
    const numberValue = Number(value);
    if (numberValue < min) {
      return Promise.reject(message);
    }
    return Promise.resolve();
  },
});

export const numberMaxRule = (max: number, message: string) => () => ({
  validator(form: any, value: string) {
    const numberValue = Number(value);
    if (numberValue > max) {
      return Promise.reject(message);
    }
    return Promise.resolve();
  },
});

export function changeBinaryUnit(
  value: string,
  targetUnit: "b" | "k" | "m" | "g" | "t" | "p" | "auto" = "g",
  defaultUnit = "b"
): number {
  let sourceUnit;
  const binaryUnits = ["b", "k", "m", "g", "t", "p", "auto"];
  const bBinaryUnits = ["B", "KiB", "MiB", "GiB", "TiB", "PiB"];
  if (value.indexOf(" ") >= 0) {
    // Has string
    const v = value.split(/(\s+)/);
    if (bBinaryUnits.includes(v[2])) {
      value = v[0] + binaryUnits[bBinaryUnits.indexOf(v[2])];
    } else {
      value = v[0];
    }
  }
  if (binaryUnits.includes(value.slice(-1))) {
    sourceUnit = value.slice(-1);
    value = value.slice(0, -1);
  } else {
    sourceUnit = defaultUnit; // Fallback
  }
  // if (targetUnit === "auto") {
  // }

  return (
    parseInt(value) *
    Math.pow(
      1024,
      Math.floor(
        binaryUnits.indexOf(sourceUnit) - binaryUnits.indexOf(targetUnit)
      )
    )
  );
}

export const extractNonNullableNodesOnConnection = <
  T extends {
    readonly edges: readonly ({
      readonly node: {
        readonly id: string;
      } | null;
    } | null)[];
  },
>(
  conn: T | null
) => {
  if (!conn) {
    return [];
  }
  return _.filter(
    _.map(conn.edges, (e) => e?.node),
    (n) => n
  ) as NonNullable<NonNullable<NonNullable<T["edges"]>[0]>["node"]>[];
};

interface LocaleTimeUnit {
  second: string;
  minute: string;
  hour: string;
  day: string;
}

export const formatTime = (seconds: number, locale = "en"): string => {
  const localeTimeUnitMap: { [key: string]: LocaleTimeUnit } = {
    en: {
      second: "s",
      minute: "m",
      hour: "h",
      day: "d",
    },
    ko: {
      second: "초",
      minute: "분",
      hour: "시간",
      day: "일",
    },
  };
  const localeTimeUnit = localeTimeUnitMap[locale] ?? localeTimeUnitMap.en;
  if (seconds <= 0) {
    return `0${localeTimeUnit.second}`;
  }
  const days = Math.floor(seconds / 86400);
  seconds -= days * 86400;
  const hours = Math.floor(seconds / 3600);
  seconds -= hours * 3600;
  const minutes = Math.floor(seconds / 60);
  seconds -= minutes * 60;
  const timeUnits: string[] = [];
  if (days > 0) {
    timeUnits.push(`${days}${localeTimeUnit.day}`);
  }
  if (hours > 0) {
    timeUnits.push(`${hours}${localeTimeUnit.hour}`);
  }
  if (minutes > 0) {
    timeUnits.push(`${minutes}${localeTimeUnit.minute}`);
  }
  if (seconds > 0) {
    timeUnits.push(`${seconds}${localeTimeUnit.second}`);
  }
  return timeUnits.join(" ");
};

export enum PipelineJobStatus {
  PENDING = "PENDING",
  RUNNING = "RUNNING",
  // WAITING = "WAITING",
  // PAUSED = "PAUSED",
  TERMINATED = "TERMINATED",
  ERROR = "ERROR",
  CANCELLING = "CANCELLING",
  CANCELLED = "CANCELLED",
  PENDING_APPROVAL = "PENDING_APPROVAL",
}

export function generateRandomString(length: number) {
  let text = "";
  const possible =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  for (let i = 0; i < length; i++) {
    text += possible.charAt(Math.floor(Math.random() * possible.length));
  }
  return text;
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

interface ContainerImageName {
  host?: string;
  port?: number;
  path?: string;
  tag?: string;
  architecture?: ImageArchitecture;
}

export const parseContainerImageName = (
  containerImageName?: string
): ContainerImageName => {
  // https://docs.docker.com/get-started/docker-concepts/building-images/build-tag-and-publish-an-image/#tagging-images
  const regex = /^([^:/]+)(:[\d]+)?(\/[^:]+)*(:?[^:@]*)?(@[^:]+)?$/;
  const match = containerImageName?.match(regex);
  if (!match) {
    return {};
  }
  const [, host, port, path, tag, architecture] = match;
  return {
    host: host,
    port: port ? Number(port.replace(":", "")) : undefined,
    path: path?.replace("/", ""),
    tag: tag?.replace(":", ""),
    architecture: architecture?.replace("@", ""),
  };
};

export const getDedicatedFolderName = (storage?: string): string | undefined =>
  storage && JSON.parse(storage).name;

export function getProjectResourceGroup(
  pipeline: PipelineYAML | undefined,
  taskEnvironment: PipelineTaskEnvironment | undefined
): string[] | undefined {
  const x =
    pipeline?.ownership?.scope === "project"
      ? pipeline.ownership.project && taskEnvironment?.["scaling-group"]
        ? [pipeline.ownership.project, taskEnvironment["scaling-group"]]
        : undefined
      : taskEnvironment?.project && taskEnvironment["scaling-group"]
        ? [taskEnvironment.project, taskEnvironment["scaling-group"]]
        : undefined;
  return x;
}

export const classNames = (...classes: (string | undefined)[]) => {
  return classes.filter(Boolean).join(" ");
};

// https://github.com/lablup/backend.ai-webui/blob/7628526b571760d50ecf8c2fa416f2398e8181b5/src/components/backend-ai-common-utils.ts#L97-L109
export function humanReadableFileSize(bytes = 0, decimalPoint = 2): string {
  if (bytes === 1) return "1 Byte";
  const k = Math.pow(10, 3);
  decimalPoint = decimalPoint < 0 ? 0 : decimalPoint;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB"];
  let i = Math.floor(Math.log(bytes) / Math.log(k));
  i = i < 0 ? 0 : i; // avoid negative value
  return (
    parseFloat((bytes / Math.pow(k, i)).toFixed(decimalPoint)) + " " + sizes[i]
  );
}

export const generateDisplayValues = <T extends number | Big>(
  convertedValue:
    | {
        number: T;
        numberFixed: string;
        unit: ByteUnit;
        value: string;
      }
    | undefined,
  {
    baseDisplayUnit,
    displayUnitSuffix,
  }: {
    baseDisplayUnit: string;
    displayUnitSuffix: string;
  }
) => {
  if (convertedValue === undefined) {
    return undefined;
  }
  const displayUnit = convertedValue.unit
    ? `${convertedValue.unit.toUpperCase()}${displayUnitSuffix}`
    : baseDisplayUnit;
  const displayValue = `${convertedValue.numberFixed} ${displayUnit}`;
  const valueAbbreviated = `${convertedValue.numberFixed}${convertedValue.unit}`;
  return {
    ...convertedValue,
    displayValue,
    displayUnit,
    valueAbbreviated,
  };
};

export function parseValueWithUnit(str: string): [number, string | undefined] {
  const match = str?.match(/^(\d*\.?\d+)\s*([a-zA-Z%]*)$/);
  if (!match) {
    // If the input doesn't match the pattern, assume it's in bytes
    return [parseFloat(str), undefined];
  }
  const num = parseFloat(match[1]);
  const unit = match[2];
  return [num, unit];
}

export function toFixedFloorWithoutTrailingZeros(
  num: number | string,
  fixed: number
) {
  const number = typeof num === "string" ? parseFloat(num) : num;
  return parseFloat(number.toFixed(fixed)).toString();
}

/**
 * Converts a value with a unit to a different unit or automatically selects the most appropriate unit.
 *
 * @param inputValue - The input string value with or without a unit (e.g., '1024m', '2g', '500').
 * @param targetUnit - The target unit to convert to ('', 'k', 'm', 'g', 't', 'p', 'e') or 'auto' for automatic unit selection.
 * @param options - Additional conversion options.
 * @param options.fixed - Number of decimal places to keep in the result. Defaults to 2.
 * @param options.round - Whether to round the result (true) or floor it (false). Defaults to false.
 * @param options.base - The base for conversion: 1024 (binary) or 1000 (decimal). Defaults to 1024.
 *
 * @returns An object containing the conversion result:
 * - `number`: The converted value as a number
 * - `numberFixed`: The converted value as a string with fixed decimal places
 * - `unit`: The target unit used for conversion
 * - `value`: A formatted string combining the number and unit
 *
 * @throws Error if the input format is invalid or the unit is not recognized
 *
 * @example
 * // Convert 1.5g to mega
 * convertUnitValue('1.5g', 'm')
 * // => { number: 1536, numberFixed: "1536", unit: "m", value: "1536m" }
 *
 * @example
 * // Auto unit selection with custom precision
 * convertUnitValue('1048576', 'auto', { fixed: 1 })
 * // => { number: 1, numberFixed: "1", unit: "m", value: "1m" }
 */
export function convertUnitValue(
  inputValue: string | undefined,
  targetUnit: ByteUnit | "auto",
  options?: {
    fixed?: number;
    round?: boolean;
    base?: 1024 | 1000;
  }
) {
  const { fixed = 2, round = false, base = 1024 } = options || {};
  if (inputValue === undefined) {
    return undefined;
  }
  // display units
  const sizes = ["", "k", "m", "g", "t", "p", "e"];
  const [sizeValue, sizeUnit] = parseValueWithUnit(inputValue);
  const sizeIndex = sizeUnit ? sizes.indexOf(sizeUnit.toLowerCase()) : 0;
  if (sizeIndex === -1 || isNaN(sizeValue)) {
    throw new Error("Invalid size format," + inputValue);
  }

  const bytes = sizeValue * Math.pow(base, sizeIndex);
  let targetIndex: number;

  if (targetUnit === "auto") {
    // Auto unit selection logic
    targetIndex = Math.floor(Math.log(bytes) / Math.log(base));
    targetIndex = Math.min(Math.max(targetIndex, 0), sizes.length - 1);

    const tempBytes = bytes / Math.pow(base, targetIndex);
    if (tempBytes < 1 && targetIndex > 0) {
      targetIndex--;
    }
  } else {
    targetIndex = targetUnit ? sizes.indexOf(targetUnit.toLowerCase()) : 0;
  }

  const finalBytes = bytes / Math.pow(base, targetIndex);
  const numberFixed = round
    ? finalBytes.toFixed(fixed)
    : toFixedFloorWithoutTrailingZeros(finalBytes, fixed);

  const unit = sizes[targetIndex] as ByteUnit;

  return {
    number: finalBytes,
    numberFixed,
    unit,
    value: `${numberFixed}${unit}`,
  };
}

/**
 * Converts a binary size value from one unit to another.
 *
 * @param inputValue - The value to convert, can be a string, number, or undefined
 * @param targetUnit - The unit to convert to, or 'auto' for automatic unit selection
 * @param fixed - The number of decimal places to fix the result to (default: 2)
 * @param round - Whether to round the result (default: false)
 * @returns An object containing the converted value information or undefined if conversion fails:
 *          - number: The converted number value
 *          - numberFixed: The formatted number with fixed decimal places
 *          - unit: The resulting unit (lowercase)
 *          - displayValue: Formatted string with value and unit (e.g., "10.24 KiB")
 *          - displayUnit: The formatted unit string (e.g., "KiB")
 */
export function convertToBinaryUnit(
  inputValue: string | number | undefined,
  targetUnit: ByteUnit | "auto",
  fixed = 2,
  round = false
) {
  inputValue = _.isNumber(inputValue) ? _.toString(inputValue) : inputValue;
  return generateDisplayValues(
    convertUnitValue(inputValue, targetUnit, {
      fixed,
      round,
      base: 1024,
    }),
    {
      baseDisplayUnit: "BiB",
      displayUnitSuffix: "iB",
    }
  );
}

/**
 * Gets the local timezone of the user's browser.
 * @returns The local timezone of the user's browser, or "UTC" if it cannot be determined.
 */
export function getLocalTimezone(): string {
  const localTimeZone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
  return localTimeZone || "UTC";
}
